/**
 * Display units for the UI kit (energy unit, currency), provided once by the shell from the
 * resolved settings. Kept apart from the store so the kit renders in tests and stories with
 * no store at all (defaults: kcal, USD, symbol).
 */
import type { ReactNode } from 'react'
import { createContext, useContext } from 'react'
import type { EnergyUnit, MassUnit } from '../engine'
import type { CurrencyDisplay } from './format'
import { setDisplayMass } from './format'

export interface Units {
  energy: EnergyUnit
  currency: string
  currencyDisplay: CurrencyDisplay
  /** How mass and density are shown. Absent: grams. */
  mass?: MassUnit
}

export const DEFAULT_UNITS: Units = {
  energy: 'kcal',
  currency: 'USD',
  currencyDisplay: 'symbol',
  mass: 'g',
}

const UnitsContext = createContext<Units>(DEFAULT_UNITS)

/**
 * Also sets the mass unit the plain formatters (`formatGrams`, `formatDensity`) use, during
 * render and before any child renders, so every gram figure in the app follows the setting
 * without each call site threading it through.
 */
export function UnitsProvider({ value, children }: { value: Units; children: ReactNode }) {
  setDisplayMass(value.mass ?? 'g')
  return <UnitsContext.Provider value={value}>{children}</UnitsContext.Provider>
}

export function useUnits(): Units {
  return useContext(UnitsContext)
}
